import { useEffect, useState } from "react";
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from "react-beautiful-dnd";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { ICategory } from "../../interfaces/category";
import Layout from "../../components/Layout/Layout";
import { showToastNotification } from "../../utility/helper";

export default function CategoriesManager() {
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // ---- reorder helper ----
    const reorder = (list: ICategory[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result.map((c, i) => ({ ...c, order: i }));
    };

    // ---- YOUR EXISTING API LOGIC ----
    const getCategories = async () => {
        setLoading(true);
        try {
            const response = await callApi(
                API_URL_2 + API_ENDPOINT.page + `categories/?type=primary&sortType=ASC`,
                { method: "GET" }
            );

            const normalized = response
                .map((c: ICategory, i: number) => ({ ...c, order: c.order ?? i }))
                .sort((a: ICategory, b: ICategory) => a.order - b.order);

            setCategories(normalized);
        } catch (error: any) {
            console.error("Failed to fetch categories:", error?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCategories();
    }, []);

    // ---- SAVE ORDER ----
    const saveOrder = async (ordered: ICategory[]) => {
        setSaving(true);
        try {
            const payload = ordered.map((c) => ({ id: c.id, order: c.order }));

            await callApi(
                API_URL_2 + API_ENDPOINT.page + "categories/reorder/items",
                {
                    method: "PUT",
                    data: payload,
                }
            );
            showToastNotification('success', 'Category order saved successfully');
        } catch (error: any) {
            console.error("Failed to save category order:", error?.message);
        } finally {
            setSaving(false);
        }
    };

    // ---- DND HANDLER ----
    const onDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const reordered = reorder(
            categories,
            result.source.index,
            result.destination.index
        );
        console.log("result onDragEnd:", result, reordered);
        setCategories(reordered);
    };

    return (
        <Layout>
            <div className="container-fluid py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Primary Categories</h4>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => saveOrder(categories)}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Order"}
                    </button>
                </div>

                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="categories">
                            {(provided) => (
                                <ul
                                    className="list-group"
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{ maxHeight: "100%" }}
                                >
                                    {categories.map((cat, index) => (
                                        <Draggable
                                            key={cat.id}
                                            draggableId={cat.id}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <li
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className={`list-group-item d-flex justify-content-between align-items-center ${snapshot.isDragging ? "shadow border-primary" : ""
                                                        }`}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        cursor: "grab",
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <img
                                                            src={cat.image}
                                                            alt={cat.title}
                                                            width={50}
                                                            height={50}
                                                            className="rounded me-3"
                                                        />
                                                        <strong>{cat.title}</strong>
                                                    </div>
                                                    <span className="text-muted">#{cat.order}</span>
                                                </li>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </ul>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </div>
        </Layout>
    );
}